import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchJson } from '@heartinternet/hi-ui';
import type {
  Service,
  ServiceType,
  UpgradeNotification,
  UpgradeStatusResponse,
  UpgradeStatusValue,
} from './types';

const SERVICES_ENDPOINT = '/cp/api/v1/services/';
const UPGRADE_STATUS_PATH = 'upgrade/status';
const POLL_INTERVAL = 5000;

const TERMINAL_STATUSES: UpgradeStatusValue[] = ['SUCCESS', 'PAYMENT_FAILED', 'UPGRADE_FAILED'];

function statusToNotificationType(status: UpgradeStatusValue): UpgradeNotification['type'] {
  if (status === 'SUCCESS') return 'complete';
  if (status === 'PAYMENT_FAILED' || status === 'UPGRADE_FAILED') return 'error';
  return 'in_progress';
}

function getPrimaryDomainName(service: Service): string {
  const primary = service.domains.find((d) => d.is_primary) ?? service.domains[0];
  return primary?.domain_name ?? service.common_name;
}

export function useUpgradeNotifications(
  serviceType: ServiceType,
  services: Service[],
  csrfToken: string,
  enabled: boolean
): {
  notifications: UpgradeNotification[];
  dismissNotification: (serviceId: string) => void;
} {
  const [notifications, setNotifications] = useState<UpgradeNotification[]>([]);
  const pollingRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const dismissedRef = useRef<Set<string>>(new Set());
  const checkStatusRef = useRef<((service: Service) => Promise<void>) | null>(null);

  const stopPolling = useCallback((serviceId: string) => {
    const timer = pollingRef.current.get(serviceId);
    if (timer) {
      clearInterval(timer);
      pollingRef.current.delete(serviceId);
    }
  }, []);

  const checkStatus = useCallback(
    async (service: Service) => {
      if (dismissedRef.current.has(service.id)) return;

      const { data, error } = await fetchJson<UpgradeStatusResponse>(
        `${SERVICES_ENDPOINT}${serviceType}/${service.id}/${UPGRADE_STATUS_PATH}`,
        { headers: { 'X-CSRF-Token': csrfToken } }
      );

      if (dismissedRef.current.has(service.id)) return;

      if (error || !data) {
        setNotifications((prev) => prev.filter((n) => n.serviceId !== service.id));
        stopPolling(service.id);
        return;
      }

      const isTerminal = TERMINAL_STATUSES.includes(data.status);

      if (isTerminal) {
        stopPolling(service.id);
      } else if (!pollingRef.current.has(service.id)) {
        const timer = setInterval(() => {
          void checkStatusRef.current?.(service);
        }, POLL_INTERVAL);
        pollingRef.current.set(service.id, timer);
      }

      const notif: UpgradeNotification = {
        type: statusToNotificationType(data.status),
        serviceId: service.id,
        domain: getPrimaryDomainName(service),
        fromName: data.from_product.name,
        toName: data.to_product.name,
      };

      setNotifications((prev) => {
        const idx = prev.findIndex((n) => n.serviceId === service.id);
        if (idx === -1) return [...prev, notif];
        const next = [...prev];
        next[idx] = notif;
        return next;
      });
    },
    [serviceType, csrfToken, stopPolling]
  );

  useEffect(() => {
    checkStatusRef.current = checkStatus;
  }, [checkStatus]);

  useEffect(() => {
    if (!enabled || services.length === 0) return;

    const pollingMap = pollingRef.current;
    services.forEach((service) => {
      void checkStatus(service);
    });

    return () => {
      pollingMap.forEach((t) => clearInterval(t));
      pollingMap.clear();
    };
  }, [enabled, services, checkStatus]);

  const dismissNotification = useCallback(
    (serviceId: string) => {
      dismissedRef.current.add(serviceId);
      setNotifications((prev) => prev.filter((n) => n.serviceId !== serviceId));
      stopPolling(serviceId);
    },
    [stopPolling]
  );

  return { notifications, dismissNotification };
}
